"use client";

import { createContext, useContext, useEffect, useState } from "react";
import { Session, DefaultSession } from "next-auth";
import { useSession } from "next-auth/react";

import { AdminPermissionSet } from "@/constants/admin-tabs";

// Restore AdvertiserProfile interface
interface AdvertiserProfile {
  status: "PENDING" | "APPROVED" | "REJECTED" | "SUSPENDED";
}

// Combine the base user type with our custom fields
type ExtendedUser = DefaultSession["user"] & {
  id: string;
  role: string;
  isSeedAdmin?: boolean;
  adminPermissions?: AdminPermissionSet;
  advertiserProfile?: AdvertiserProfile;
  onboardingStatus?: string;
  onboardingCompleted?: boolean;
  needsPasswordChange?: boolean;
  roles?: string[];
  username?: string;
  firstName?: string;
  lastName?: string;
  isFoundingMember?: boolean;
};

interface AuthContextType {
  isAuthenticated: boolean;
  user: ExtendedUser | null;
  setUser: (user: ExtendedUser | null) => void;
  isLoading: boolean;
  needsEmailVerification: boolean;
  setNeedsEmailVerification: (value: boolean) => void;
  pendingUser: any;
  setPendingUser: (user: any) => void;
  completeEmailVerification: () => void;
  cancelRegistration: () => void;
  forceRefreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  user: null,
  setUser: () => {},
  isLoading: true,
  needsEmailVerification: false,
  setNeedsEmailVerification: () => {},
  pendingUser: null,
  setPendingUser: () => {},
  completeEmailVerification: () => {},
  cancelRegistration: () => {},
  forceRefreshSession: async () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { data: session, status } = useSession();
  const [isLoading, setIsLoading] = useState(true);
  const [needsEmailVerification, setNeedsEmailVerification] = useState(false);
  const [pendingUser, setPendingUser] = useState<any>(null);
  const [user, setUser] = useState<ExtendedUser | null>(null);

  useEffect(() => {
    if (status === "loading") {
      // Don't clear user or show unauthenticated during refetch — keep previous state so UI doesn't glitch
      if (!user) setIsLoading(true);
    } else if (status === "unauthenticated") {
      setIsLoading(false);
      setUser(null);
    } else {
      // status === "authenticated"
      setIsLoading(false);
      setUser(session?.user as ExtendedUser | null);
    }
  }, [status, session]);

  const completeEmailVerification = () : void => {
    setNeedsEmailVerification(false);
    setPendingUser(null);
  };

  const cancelRegistration = () : void => {
    setNeedsEmailVerification(false);
    setPendingUser(null);
  };

  const forceRefreshSession = async () => {
    try {
      const response = await fetch('/api/auth/force-refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.user) {
          setUser((prev) => ({ ...(prev || {}), ...data.user } as ExtendedUser));
        }
      }
    } catch (error) {
      console.error('Failed to force refresh session:', error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        // Stay authenticated during session refetch (status "loading") so UI never glitches to logged-out
        isAuthenticated: !!user && (status === "authenticated" || status === "loading"),
        user,
        setUser,
        // Don't show loading during refetch when we already have a user (avoids glitch)
        isLoading: (isLoading || status === "loading") && !user,
        needsEmailVerification,
        setNeedsEmailVerification,
        pendingUser,
        setPendingUser,
        completeEmailVerification,
        cancelRegistration,
        forceRefreshSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
